import React, { useEffect, useMemo, useState } from "react";
import "./runlog.css";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { DismissFilled } from "@fluentui/react-icons";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_PaginationState,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import { fetchValuationHistory } from "../../../../redux/SystemManager/bulkValuationSlice";
import { capitalizeFirstLetter, formatedDate } from "../../../../utils";
import Multiselectsearch from "../../../../components/MultiSearchDropdown";
import ASCButton from "../../../../components/Button";
import moment from "moment";
import {
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Button,
  Accordion,
  AccordionItem,
  AccordionHeader,
  AccordionPanel,
  Tooltip,
} from "@fluentui/react-components";
import { TableListLoader } from "../../../../components/Loader/TableListLoader";
import DynamicDatePicker from "../../../../components/ControlledDatePicker/dynamicDatepicker";
import { FilterDismiss20Regular } from "@fluentui/react-icons";
import runLogConfig from "../../../../shared/runLogTable.json";


function RunLog() {
  const {
    valuationHistory,
    metadata,
    loadingHistory,
    investmentProviders,
    dataProviders,
  } = useSelector((state: RootState) => state.bulkValuation);
  const today = new Date();
  const [createdAt, setCreatedAt] = useState<Date | null>(today);
  const [filters, setFilters] = useState({
    createdAt: moment(today).format("YYYY-MM-DD"),
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [rowCount, setRowCount] = useState(0);
  const [valuationDate, setValuationDate] = useState<Date | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<any>([]);
  const [selectedDataProvider, setSelectedDataProvider] = useState<any>([]);
  const [selectedStatus, setSelectedStatus] = useState<any>([]);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const dispatch: AppDispatch = useDispatch();

  // Mock data provider list

  const statusData = [
    { statusId: "success", Name: "Success" },
    { statusId: "failed", Name: "Failed" },
  ];

  const columns: MRT_ColumnDef<any>[] = [
    {
      accessorKey: "CreatedAt",
      header: "Date Processed",
      Cell: ({ row }: any) => <>{formatedDate(row?.original?.CreatedAt)}</>,
    },
    {
      accessorKey: "ValuationDate",
      header: "Value Date",
      Cell: ({ row }: any) => <>{formatedDate(row?.original?.ValuationDate)}</>,
    },
    { accessorKey: "ProviderName", header: "Provider" },
    { accessorKey: "DataProviderName", header: "Data Provider" },
    {
      accessorKey: "CaseRef",
      header: "Provider Reference",
      Cell: ({ row }) => (
        <span
          style={{
            fontWeight: "bold",
            cursor: "pointer",
          }}
          onClick={() => {
            setSelectedRow(row.original);
            setOpenDialog(true);
          }}
        >
          {row.original?.CaseRef}
        </span>
      ),
    },
    {
      accessorKey: "Status",
      header: "Status",
      Cell: ({ row }) => <>{capitalizeFirstLetter(row.original?.Status)}</>,
    },
    {
      accessorKey: "StatusDetails",
      header: "Message",
      Cell: ({ row }: any) => {
        let message = "";
        try {
          const details = row.original?.StatusDetails;
          message =
            details?.status_details || details?.error_code_details?.error || "";
        } catch (e) {
          message = "";
        }
        return <>{message}</>;
      },
    },
  ];

  const getDynamicColumns = (tableId: string) => {
    const tableConfig = runLogConfig.table.find((t) => t.tableId === tableId);
    if (!tableConfig) return [];

    return tableConfig.tableColumns.map((col) => {
      // Strip prefix if exists (e.g. "input_json.case.funds.FundCode" -> "FundCode")
      const accessorKey = col.accessorKey.includes(".")
        ? col.accessorKey.split(".").pop() || col.accessorKey
        : col.accessorKey;

      const column: MRT_ColumnDef<any> = {
        accessorKey: accessorKey,
        header: col.header,
      };

      // Add date formatting for specific fields
      if (accessorKey.toLowerCase().includes("date") || accessorKey === "CreatedAt") {
        column.Cell = ({ row }: any) => (
          <>{formatedDate(row?.original?.[accessorKey])}</>
        );
      } else {
        // Handle potential arrays (e.g. FirstNames) or nested objects (e.g. customer_full_name)
        column.Cell = ({ row }: any) => {
          const value = row?.original?.[accessorKey];
          if (Array.isArray(value)) return <>{value.join(" ")}</>;
          if (typeof value === "object" && value !== null) {
            return <>{value._ || value.Name || value.name || JSON.stringify(value)}</>;
          }
          return <>{value ?? ""}</>;
        };
      }

      return column;
    });
  };



  const providerOptions =
    investmentProviders?.map((provider) => ({
      label: provider.Name,
      value: provider.InvestmentId,
    })) ?? [];

  const dataProviderOptions = dataProviders?.map((dp) => ({
    value: dp.DataProviderId,
    label: dp.Name,
  }));

  const statusOptions = statusData.map((st) => ({
    value: st.statusId,
    label: st.Name,
  }));

  const onFormatDate = (date?: Date): string =>
    !date ? "" : moment(date).format("DD-MM-YYYY");

  const isFilterApplied = useMemo(() => {
    const todayStr = moment(today).format("YYYY-MM-DD");
    const createdAtStr = createdAt
      ? moment(createdAt).format("YYYY-MM-DD")
      : "";

    return (
      createdAtStr !== todayStr ||
      valuationDate !== null ||
      selectedProvider.length > 0 ||
      selectedDataProvider.length > 0 ||
      selectedStatus.length > 0
    );
  }, [
    createdAt,
    valuationDate,
    selectedProvider,
    selectedDataProvider,
    selectedStatus,
    today,
  ]);

  const clearFilter = () => {
    const today = new Date();

    // 🔹 Reset UI states
    setCreatedAt(today);
    setValuationDate(null);
    setSelectedProvider([]);
    setSelectedDataProvider([]);
    setSelectedStatus([]);

    // 🔹 Reset pagination
    setPagination((prev) => ({
      ...prev,
      pageIndex: 0,
    }));

    // 🔹 Reset filters (default state)
    setFilters({
      createdAt: moment(today).format("YYYY-MM-DD"),
    });
  };

  const getErrorLogs = () => {
    const payload: any = {};

    if (createdAt) {
      payload.createdAt = moment(createdAt).format("YYYY-MM-DD");
    }
    if (valuationDate) {
      payload.valuationDate = moment(valuationDate).format("YYYY-MM-DD");
    }
    if (selectedProvider.length > 0) {
      const provider = providerOptions.find(
        (option) => option.value === selectedProvider[0],
      );
      payload.providerName = provider ? provider.label : selectedProvider[0];
    }
    if (selectedDataProvider.length > 0) {
      const dp = dataProviderOptions?.find(
        (option) => option.value === selectedDataProvider[0],
      );
      payload.dataProviderName = dp ? dp.label : selectedDataProvider[0];
    }
    if (selectedStatus.length > 0) {
      const status = statusOptions.find(
        (option) => option.value === selectedStatus[0],
      );
      payload.status = status ? status.value : selectedStatus[0];
    }
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    setFilters(payload); // 👈 save filters
  };

  // Initial load
  useEffect(() => {
    const payload = {
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
      ...filters, // 👈 merge filters
    };

    dispatch(fetchValuationHistory(payload));
  }, [dispatch, pagination.pageIndex, pagination.pageSize, filters]);

  // Update row count when metadata changes
  useEffect(() => {
    if (metadata?.totalRecords !== undefined) {
      setRowCount(metadata.totalRecords);
    }
  }, [metadata]);

  const parseStatusDetails = (details: string) => {
    try {
      return JSON.parse(details);
    } catch (e) {
      return null;
    }
  };

  const renderStatusDetails = () => {
    if (!selectedRow?.StatusDetails) {
      return <p>No status details available</p>;
    }

    // Parse StatusDetails
    const details = selectedRow?.StatusDetails;
    let parsed = typeof details === "string" ? JSON.parse(details) : details;

    const caseData = parsed?.input_json?.case ?? {};

    const renderedTables = runLogConfig.table.filter((tableItem) => {
      const actualKey = Object.keys(caseData).find(
        (key) => key.toLowerCase() === tableItem.tableId.toLowerCase()
      );
      const tableData = actualKey ? caseData[actualKey] : [];
      const normalizedData = Array.isArray(tableData) ? tableData : [tableData];
      return !(
        normalizedData.length === 0 ||
        normalizedData[0] === null ||
        (normalizedData.length === 1 &&
          typeof normalizedData[0] === "object" &&
          Object.keys(normalizedData[0]).length === 0)
      );
    });

    return (
      <Accordion
        key={selectedRow?.HistoryId}
        defaultOpenItems={renderedTables.length > 0 ? [renderedTables[0].tableId] : []}
        multiple
      >
        {renderedTables.map((tableItem) => {
          const actualKey = Object.keys(caseData).find(
            (key) => key.toLowerCase() === tableItem?.tableId?.toLowerCase()
          );
          const tableData = actualKey ? caseData[actualKey] : [];
          const normalizedData = Array.isArray(tableData) ? tableData : [tableData];

          return (
            <AccordionItem key={tableItem.tableId} value={tableItem.tableId}>
              <AccordionHeader>
                <b>{tableItem.tableName}</b>
              </AccordionHeader>
              <AccordionPanel>
                <MaterialReactTable
                  columns={getDynamicColumns(tableItem.tableId)}
                  data={normalizedData}
                  enablePagination={false}
                  enableColumnActions={false}
                  enableSorting={false}
                  enableTopToolbar={false}
                  enableBottomToolbar={false}
                />
              </AccordionPanel>
            </AccordionItem>
          );
        })}
      </Accordion>
    );
  };


  return (
    <div className="runlogs">
      {loadingHistory ? (
        <TableListLoader rows={10} columns={6} />
      ) : (
        <MaterialReactTable
          columns={columns}
          data={valuationHistory ?? []}
          renderTopToolbarCustomActions={() => (
            <div className="runlogs_filter">
              <DynamicDatePicker
                border
                width={150}
                name="createdAt"
                label={null}
                required={false}
                disabled={false}
                formik={{
                  values: {
                    createdAt: createdAt ?? null,
                  },
                  setFieldValue: (_name: string, value: Date | null) => {
                    setCreatedAt(value);
                  },
                  setFieldTouched: () => { },
                  setFieldError: () => { },
                  errors: {},
                  touched: {},
                }}
              />
              <DynamicDatePicker
                border
                width={150}
                name="valuationDate"
                label={null}
                required={false}
                disabled={false}
                formik={{
                  values: {
                    valuationDate: valuationDate ?? null,
                  },
                  setFieldValue: (_name: string, value: Date | null) => {
                    setValuationDate(value);
                  },
                  setFieldTouched: () => { },
                  setFieldError: () => { },
                  errors: {},
                  touched: {},
                }}
              />

              {/* Provider */}
              <Multiselectsearch
                setSelectedDropDownValues={setSelectedProvider}
                selectedDropDownValues={selectedProvider}
                placeholder="Select Provider"
                options={providerOptions}
                select_all={false}
                multiselect={false}
              />

              {/* Data Provider */}
              <Multiselectsearch
                setSelectedDropDownValues={setSelectedDataProvider}
                selectedDropDownValues={selectedDataProvider}
                placeholder="Select Data Provider"
                options={
                  dataProviderOptions as { label: string; value: string }[]
                }
                select_all={false}
                multiselect={false}
              />

              {/* Status */}
              <Multiselectsearch
                setSelectedDropDownValues={setSelectedStatus}
                selectedDropDownValues={selectedStatus}
                placeholder="Status"
                options={statusOptions}
                select_all={false}
                multiselect={false}
              />

              {/* Apply Filter */}
              <ASCButton
                shape="rounded"
                appearance="primary"
                className="asc-button-primary"
                label="Apply"
                onClick={getErrorLogs}
              />
              {isFilterApplied && (
                <Tooltip
                  content="Clear filter"
                  positioning="below"
                  relationship="label"
                >
                  <Button
                    shape="rounded"
                    icon={<FilterDismiss20Regular />}
                    size="medium"
                    onClick={clearFilter}
                  />
                </Tooltip>
              )}
            </div>
          )}
          enablePagination={true}
          state={{ pagination }}
          rowCount={rowCount}
          onPaginationChange={setPagination}
          manualPagination={true}
          renderEmptyRowsFallback={() => (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "calc(100vh - 375px)",
                color: "#f00",
                width: "calc(100dvw - 300px)",
              }}
            >
              <span>
                No valuation files have been processed for the selected period
              </span>
            </div>
          )}
        />
      )}
      <Dialog
        open={openDialog}
        onOpenChange={(_, data) => setOpenDialog(data.open)}
      >
        <DialogSurface style={{ maxWidth: "70vw" }}>
          <DialogBody>
            <DialogTitle>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {selectedRow?.ProviderName} - {selectedRow?.CaseRef}
                <DismissFilled onClick={() => setOpenDialog(false)} />
              </div>
            </DialogTitle>
            <DialogContent>
              <DialogContent>{renderStatusDetails()}</DialogContent>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}

export default RunLog;
